/**
 * Purchase Order Status Extensions Client - Business Logic Layer
 *
 * Provides extended functionality for purchase order statuses beyond generated CRUD operations
 */

import { PurchaseOrderStatusesClient } from '../generated_clients/generated-purchase_order_statuses-api-client.js';
import type { PurchaseOrderStatuses } from '../generated_schemas/generated-purchase_order_statuses-schema.js';
import { httpClient } from '../http-client.js';

/**
 * Extended client for purchase order status operations
 */
export class PurchaseOrderStatusExtensionsClient {
  constructor(private purchaseOrderStatusesClient: PurchaseOrderStatusesClient) {}

  /**
   * Get all purchase order statuses for an organization
   */
  async getPurchaseOrderStatusesByOrganization(organizationId: string): Promise<{
    data: PurchaseOrderStatuses[];
    success: boolean;
  }> {
    const response = await httpClient.get<{
      data: PurchaseOrderStatuses[];
      success: boolean;
    }>(`/purchase-order-statuses?organization_id=${organizationId}`);

    return response;
  }

  /**
   * Get default purchase order status for an organization
   */
  async getDefaultPurchaseOrderStatus(organizationId: string): Promise<{
    data: PurchaseOrderStatuses | null;
    success: boolean;
  }> {
    const response = await httpClient.get<{
      data: PurchaseOrderStatuses | null;
      success: boolean;
    }>(`/purchase-order-statuses/default?organization_id=${organizationId}`);

    return response;
  }

  // Logging utility
  private log(message: string, data?: any): void {
    console.log(`[PurchaseOrderStatusExtensions] ${message}`, data || '');
  }
}

// Factory function
export const createPurchaseOrderStatusExtensions = (
  purchaseOrderStatusesClient: PurchaseOrderStatusesClient
): PurchaseOrderStatusExtensionsClient =>
  new PurchaseOrderStatusExtensionsClient(purchaseOrderStatusesClient);

// Export singleton
const purchaseOrderStatusesClient = new PurchaseOrderStatusesClient();
export const purchaseOrderStatusExtensions = createPurchaseOrderStatusExtensions(
  purchaseOrderStatusesClient
);
